/**
 * Help — field manual for workspace tasks.
 *
 * THESIS: Pick a task, match the plate, click the named control. Refuses a
 * card grid of topics and a marketing docs site.
 * OWN-WORLD: Nautilus Console — Deep Ocean ink, Deck Sand active mark,
 * 1px panel borders, uppercase tracked labels, Roboto.
 * STORY: A new engineer finds the job, sees the real screen, and acts.
 * FIRST VIEWPORT: Help + guide name, task rail on the left, procedure and
 * screenshot in the reading column.
 * FORM: Specified two-column reader inside the established console.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HELP_GUIDES, guideForUser } from '../help/guides';
import type { HelpGuide, HelpStep, HelpTask } from '../help/types';

const NAUTILUS = {
  deepOcean: '#0f2134',
  sand: '#ccba9b',
  abyssal: '#0a1622',
};

const HelpPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surface = isDark ? NAUTILUS.deepOcean : '#ffffff';
  const ink = isDark ? '#ffffff' : NAUTILUS.deepOcean;
  const muted = isDark ? '#b0bec5' : '#546e7a';

  const initialGuide = useMemo(
    () => guideForUser(user?.roles),
    [user?.roles],
  );

  const requestedGuideId = searchParams.get('guide');
  const guide: HelpGuide =
    HELP_GUIDES.find((item) => item.id === requestedGuideId) ?? initialGuide;

  const requestedTaskId = searchParams.get('task');
  const task: HelpTask =
    guide.tasks.find((item) => item.id === requestedTaskId) ?? guide.tasks[0];

  const [lightbox, setLightbox] = useState<HelpStep | null>(null);

  useEffect(() => {
    if (searchParams.get('guide') === guide.id && searchParams.get('task') === task.id) {
      return;
    }
    setSearchParams(
      { guide: guide.id, task: task.id },
      { replace: true },
    );
  }, [guide.id, task.id, searchParams, setSearchParams]);

  const openTask = (next: HelpTask) => {
    setSearchParams({ guide: guide.id, task: next.id });
  };

  const pageFillSx = {
    height: `calc(100vh - ${theme.spacing(10)} - ${theme.spacing(4)})`,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
  };

  return (
    <Box sx={pageFillSx} component="article">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 600, letterSpacing: '-0.01em', color: ink }}
          >
            Help
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontFamily: '"Roboto Mono", ui-monospace, monospace',
              fontSize: '0.8125rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: muted,
            }}
          >
            {guide.title}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              maxWidth: '70ch',
              color: muted,
              fontSize: '0.875rem',
              lineHeight: 1.5,
            }}
          >
            {guide.intro}
          </Typography>
        </Box>
        {HELP_GUIDES.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="help-guide-label">Guide</InputLabel>
            <Select
              labelId="help-guide-label"
              label="Guide"
              value={guide.id}
              onChange={(event) => {
                const next = HELP_GUIDES.find((item) => item.id === event.target.value);
                if (!next) return;
                setSearchParams({ guide: next.id, task: next.tasks[0].id });
              }}
            >
              {HELP_GUIDES.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: isCompact ? 'column' : 'row',
          border: `1px solid ${panelBorder}`,
          borderRadius: 1,
          bgcolor: surface,
          overflow: 'hidden',
        }}
      >
        <Box
          component="nav"
          aria-label="Help tasks"
          sx={{
            width: isCompact ? '100%' : 260,
            flexShrink: 0,
            borderRight: isCompact ? 'none' : `1px solid ${panelBorder}`,
            borderBottom: isCompact ? `1px solid ${panelBorder}` : 'none',
            overflow: isCompact ? 'auto hidden' : 'auto',
            bgcolor: isDark ? NAUTILUS.abyssal : alpha(NAUTILUS.deepOcean, 0.02),
          }}
        >
          {isCompact ? (
            <Box sx={{ display: 'flex', gap: 1, p: 1.5, overflowX: 'auto' }}>
              {guide.tasks.map((item) => {
                const selected = item.id === task.id;
                return (
                  <Button
                    key={item.id}
                    onClick={() => openTask(item)}
                    aria-current={selected ? 'page' : undefined}
                    sx={{
                      flexShrink: 0,
                      borderRadius: 1,
                      boxShadow: 'none',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      px: 1.5,
                      py: 0.75,
                      border: `1px solid ${selected ? NAUTILUS.sand : panelBorder}`,
                      bgcolor: selected ? alpha(NAUTILUS.sand, isDark ? 0.18 : 0.35) : 'transparent',
                      color: selected ? ink : muted,
                      '&:hover': {
                        boxShadow: 'none',
                        bgcolor: alpha(NAUTILUS.sand, 0.12),
                      },
                    }}
                  >
                    {item.title}
                  </Button>
                );
              })}
            </Box>
          ) : (
            <List dense disablePadding sx={{ py: 1 }}>
              {guide.tasks.map((item) => {
                const selected = item.id === task.id;
                return (
                  <ListItemButton
                    key={item.id}
                    selected={selected}
                    onClick={() => openTask(item)}
                    aria-current={selected ? 'page' : undefined}
                    sx={{
                      mx: 1,
                      mb: 0.25,
                      borderRadius: 1,
                      py: 1,
                      borderLeft: selected ? `3px solid ${NAUTILUS.sand}` : '3px solid transparent',
                      bgcolor: selected ? alpha(NAUTILUS.sand, isDark ? 0.12 : 0.22) : 'transparent',
                      '&.Mui-selected': {
                        bgcolor: alpha(NAUTILUS.sand, isDark ? 0.12 : 0.22),
                      },
                      '&.Mui-selected:hover, &:hover': {
                        bgcolor: alpha(NAUTILUS.sand, isDark ? 0.18 : 0.3),
                      },
                      '&:focus-visible': {
                        outline: `2px solid ${NAUTILUS.sand}`,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: '0.8125rem',
                        fontWeight: selected ? 600 : 500,
                        color: selected ? ink : muted,
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>

        <Box
          component="section"
          aria-labelledby="help-task-title"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'auto',
            p: { xs: 2, md: 3 },
            '& ::selection': {
              backgroundColor: alpha(NAUTILUS.sand, 0.35),
              color: NAUTILUS.deepOcean,
            },
          }}
        >
          <Typography
            id="help-task-title"
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, letterSpacing: '-0.01em', color: ink, mb: 1 }}
          >
            {task.title}
          </Typography>
          <Typography
            sx={{
              color: muted,
              maxWidth: '70ch',
              mb: 3,
              fontSize: '0.9375rem',
              lineHeight: 1.5,
            }}
          >
            {task.goal}
          </Typography>

          <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {task.steps.map((step, index) => (
              <Box
                component="li"
                key={`${task.id}-${index}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '2rem 1fr',
                  columnGap: 2,
                  mb: 4,
                  '&:last-of-type': { mb: 0 },
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    border: `1px solid ${panelBorder}`,
                    display: 'grid',
                    placeItems: 'center',
                    fontFamily: '"Roboto Mono", ui-monospace, monospace',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                    bgcolor: isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.deepOcean, 0.04),
                  }}
                >
                  {index + 1}
                </Box>
                <Box>
                  <Typography
                    sx={{
                      color: ink,
                      maxWidth: '70ch',
                      mb: 1.5,
                      fontSize: '0.9375rem',
                      lineHeight: 1.55,
                    }}
                  >
                    {step.instruction}
                  </Typography>
                  <StepPlate step={step} muted={muted} panelBorder={panelBorder} onOpen={setLightbox} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Dialog
        open={Boolean(lightbox)}
        onClose={() => setLightbox(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            bgcolor: surface,
            border: `1px solid ${panelBorder}`,
            boxShadow: 'none',
          },
        }}
      >
        {lightbox && (
          <Box sx={{ p: 1.5 }}>
            <Box
              component="img"
              src={lightbox.image}
              alt={lightbox.caption}
              sx={{
                display: 'block',
                width: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: 1,
                border: `1px solid ${panelBorder}`,
              }}
            />
            <Typography sx={{ mt: 1.5, color: muted, fontSize: '0.8125rem' }}>
              {lightbox.caption}
            </Typography>
            <Button
              onClick={() => setLightbox(null)}
              sx={{
                mt: 1,
                color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            >
              Close
            </Button>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

const StepPlate: React.FC<{
  step: HelpStep;
  muted: string;
  panelBorder: string;
  onOpen: (step: HelpStep) => void;
}> = ({ step, muted, panelBorder, onOpen }) => {
  const [failed, setFailed] = useState(false);

  return (
    <Box component="figure" sx={{ m: 0 }}>
      <Box
        component="button"
        type="button"
        onClick={() => {
          if (!failed) onOpen(step);
        }}
        aria-label={failed ? step.caption : `Enlarge: ${step.caption}`}
        sx={{
          display: 'block',
          width: '100%',
          p: 0,
          m: 0,
          border: `1px solid ${panelBorder}`,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: alpha(NAUTILUS.deepOcean, 0.04),
          cursor: failed ? 'default' : 'zoom-in',
          textAlign: 'left',
          '&:focus-visible': {
            outline: `2px solid ${NAUTILUS.sand}`,
            outlineOffset: 2,
          },
        }}
      >
        {failed ? (
          <Box sx={{ px: 2, py: 6, textAlign: 'center' }}>
            <Typography sx={{ color: muted, fontSize: '0.875rem' }}>
              Screenshot unavailable
            </Typography>
          </Box>
        ) : (
          <Box
            component="img"
            src={step.image}
            alt={step.caption}
            loading="lazy"
            onError={() => setFailed(true)}
            sx={{
              display: 'block',
              width: '100%',
              maxHeight: 420,
              objectFit: 'contain',
              objectPosition: 'top left',
              bgcolor: '#0a1622',
            }}
          />
        )}
      </Box>
      <Typography
        component="figcaption"
        sx={{
          mt: 1,
          color: muted,
          fontSize: '0.8125rem',
          lineHeight: 1.4,
        }}
      >
        {step.caption}
      </Typography>
    </Box>
  );
};

export default HelpPage;
